import * as THREE from 'three';

const SKY_VERT = /* glsl */`
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const SKY_FRAG = /* glsl */`
  precision highp float;
  uniform float uTime;
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  uniform vec3 uGlow;
  uniform vec3 uAurora;
  uniform float uStars;
  uniform float uOctaves;
  varying vec3 vDir;

  float hash(vec3 p) {
    p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }
  float noise(vec3 x) {
    vec3 i = floor(x); vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(hash(i + vec3(0,0,0)), hash(i + vec3(1,0,0)), f.x),
                   mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y),
               mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x),
                   mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y), f.z);
  }
  float fbm(vec3 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 5; i++) {
      if (float(i) >= uOctaves) break;
      v += a * noise(p);
      p *= 2.03; a *= 0.5;
    }
    return v;
  }

  void main() {
    vec3 d = normalize(vDir);
    float up = clamp(d.y * 0.5 + 0.5, 0.0, 1.0);

    // Vertical gradient, biased so the horizon band is thin and hot.
    vec3 col = mix(uHorizon, uZenith, pow(up, 0.55));

    // Horizon bloom, strongest toward the city core behind -Z.
    float band = exp(-abs(d.y) * 7.0);
    float facing = pow(max(0.0, dot(normalize(vec3(d.x, 0.0, d.z)), vec3(0.35, 0.0, -0.94))) , 2.0);
    col += uGlow * band * (0.35 + 0.9 * facing);

    // Stars, culled below the horizon and dimmed inside the glow.
    if (d.y > 0.0) {
      vec3 sp = d * 190.0;
      vec3 cell = floor(sp);
      float h = hash(cell);
      if (h > 0.9905) {
        vec3 c = (cell + 0.5) / 190.0;
        float dist = length(normalize(c) - d) * 340.0;
        float tw = 0.55 + 0.45 * sin(uTime * (1.5 + h * 6.0) + h * 40.0);
        float s = exp(-dist * dist) * tw * uStars;
        col += vec3(0.75, 0.9, 1.0) * s * smoothstep(0.0, 0.25, d.y);
      }
    }

    // Smog decks drifting across the upper sky.
    float clouds = fbm(vec3(d.xz * 2.4 / max(abs(d.y) + 0.22, 0.22), uTime * 0.012));
    float cmask = smoothstep(0.05, 0.65, d.y) * smoothstep(0.35, 0.8, clouds);
    col = mix(col, mix(uHorizon, uAurora, 0.35) * 1.15, cmask * 0.45);

    // Aurora ribbons: slow vertical curtains, the one bright cool element.
    float ribbon = fbm(vec3(d.x * 2.0 + uTime * 0.04, d.y * 5.0, d.z * 2.0 - uTime * 0.03));
    float rmask = smoothstep(0.52, 0.85, ribbon) * smoothstep(-0.05, 0.45, d.y) * (1.0 - cmask);
    col += uAurora * rmask * 0.18;

    // A little dither stops the gradient from banding on 8 bit displays.
    col += (hash(d * 991.0) - 0.5) * 0.008;

    gl_FragColor = vec4(col, 1.0);
  }
`;

function windowTexture(size = 256) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#05070d';
  ctx.fillRect(0, 0, size, size);
  const cols = 12, rows = 26;
  const cw = size / cols, ch = size / rows;
  const tints = ['#2ff3ff', '#ff2fd0', '#ffb020', '#9dff3c', '#7fa8ff'];
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (Math.random() > 0.42) continue;
      ctx.globalAlpha = 0.25 + Math.random() * 0.75;
      ctx.fillStyle = tints[(Math.random() * tints.length) | 0];
      ctx.fillRect(x * cw + cw * 0.22, y * ch + ch * 0.22, cw * 0.56, ch * 0.5);
    }
  }
  ctx.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

const GRID_FRAG = /* glsl */`
  precision highp float;
  uniform vec3 uColor;
  uniform vec3 uColor2;
  uniform float uTime;
  uniform float uSize;
  varying vec2 vXZ;
  float gridLine(vec2 p, float scale, float w) {
    vec2 g = abs(fract(p / scale - 0.5) - 0.5) / fwidth(p / scale);
    return 1.0 - min(min(g.x, g.y) / w, 1.0);
  }
  void main() {
    float d = length(vXZ);
    float fade = 1.0 - smoothstep(uSize * 0.12, uSize * 0.55, d);
    if (fade <= 0.001) discard;
    float fine = gridLine(vXZ, 24.0, 1.4);
    float bold = gridLine(vXZ, 192.0, 2.0);
    float pulse = 0.65 + 0.35 * sin(uTime * 0.6 - d * 0.006);
    vec3 col = uColor * fine * 0.45 + uColor2 * bold * 1.1;
    gl_FragColor = vec4(col * pulse, (fine * 0.5 + bold) * fade);
  }
`;

/**
 * The backdrop: sky dome, a distant skyline, drifting traffic and the
 * luminous city floor far below. All of it exists to make height legible,
 * which is the whole point of a game about falling on purpose.
 */
export class Sky {
  constructor(tier, palette = {}) {
    this.tier = tier;
    this.group = new THREE.Group();
    // The dome is infinitely far away so it tracks the camera on all axes.
    // The skyline does not: climbing above the towers has to actually read as
    // climbing above the towers.
    this.skyGroup = new THREE.Group();
    this.farGroup = new THREE.Group();
    this.group.add(this.skyGroup, this.farGroup);

    const zenith = new THREE.Color(palette.zenith ?? 0x06030f);
    const horizon = new THREE.Color(palette.horizon ?? 0x1a0b2e);
    const glow = new THREE.Color(palette.glow ?? 0x6a1550);
    const aurora = new THREE.Color(palette.aurora ?? 0x0e6f8a);

    // --- dome
    this.domeMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uZenith: { value: zenith },
        uHorizon: { value: horizon },
        uGlow: { value: glow },
        uAurora: { value: aurora },
        uStars: { value: tier.starCount > 1200 ? 1.0 : 0.65 },
        uOctaves: { value: tier.cityDepth >= 3 ? 4 : 2 },
      },
      vertexShader: SKY_VERT,
      fragmentShader: SKY_FRAG,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      fog: false,
      toneMapped: false,
    });
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(1, 40, 24), this.domeMat);
    this.dome.scale.setScalar(760);
    this.dome.renderOrder = -1000;
    this.dome.frustumCulled = false;
    this.skyGroup.add(this.dome);

    // --- skyline
    this.windows = windowTexture(tier.envRes >= 512 ? 256 : 128);
    const count = tier.cityDepth * 46;
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshBasicMaterial({ map: this.windows, toneMapped: false, fog: true });
    this.city = new THREE.InstancedMesh(geo, mat, count);
    this.city.frustumCulled = false;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3();
    const p = new THREE.Vector3();
    for (let i = 0; i < count; i++) {
      const ring = Math.floor(i / 46);
      const radius = 320 + ring * 190 + Math.random() * 150;
      const angle = Math.random() * Math.PI * 2;
      const height = (28 + Math.random() * 190) * (1 + ring * 0.35);
      const width = 22 + Math.random() * 46;
      p.set(Math.cos(angle) * radius, height * 0.5 - 190, Math.sin(angle) * radius);
      s.set(width, height, width * (0.7 + Math.random() * 0.6));
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.random() * Math.PI);
      m.compose(p, q, s);
      this.city.setMatrixAt(i, m);
    }
    this.city.instanceMatrix.needsUpdate = true;
    this.farGroup.add(this.city);

    // --- air traffic
    if (tier.particles > 0.5) {
      const tcount = Math.floor(260 * tier.particles);
      const pos = new Float32Array(tcount * 3);
      const seed = new Float32Array(tcount);
      for (let i = 0; i < tcount; i++) {
        pos[i * 3] = 0; pos[i * 3 + 1] = 0; pos[i * 3 + 2] = 0;
        seed[i] = Math.random();
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
      this.trafficMat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 } },
        vertexShader: /* glsl */`
          attribute float aSeed;
          uniform float uTime;
          varying float vT;
          void main() {
            float lane = floor(aSeed * 7.0);
            float r = 260.0 + lane * 96.0;
            float y = -140.0 + lane * 58.0 + sin(aSeed * 31.0) * 22.0;
            float dir = mod(lane, 2.0) < 1.0 ? 1.0 : -1.0;
            float a = aSeed * 628.3 + uTime * (0.02 + aSeed * 0.03) * dir;
            vec3 p = vec3(cos(a) * r, y, sin(a) * r);
            vT = fract(aSeed * 13.0);
            vec4 mv = modelViewMatrix * vec4(p, 1.0);
            gl_PointSize = 2.5;
            gl_Position = projectionMatrix * mv;
          }
        `,
        fragmentShader: /* glsl */`
          precision mediump float;
          varying float vT;
          void main() {
            vec2 c = gl_PointCoord - 0.5;
            if (length(c) > 0.5) discard;
            vec3 col = vT > 0.5 ? vec3(1.0, 0.35, 0.25) : vec3(0.4, 0.95, 1.0);
            gl_FragColor = vec4(col * 1.8, 0.85);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      this.traffic = new THREE.Points(g, this.trafficMat);
      this.traffic.frustumCulled = false;
      this.farGroup.add(this.traffic);
    }

    // --- city floor: gives the void a bottom you can see but never reach
    this.gridMat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0x1f7fa0) },
        uColor2: { value: new THREE.Color(0xff2fd0) },
        uTime: { value: 0 },
        uSize: { value: 4200 },
      },
      vertexShader: /* glsl */`
        varying vec2 vXZ;
        void main() {
          vXZ = position.xy;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: GRID_FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: false,
      toneMapped: false,
    });
    this.floorGrid = new THREE.Mesh(new THREE.PlaneGeometry(4200, 4200, 1, 1), this.gridMat);
    this.floorGrid.rotation.x = -Math.PI / 2;
    this.floorGrid.frustumCulled = false;
    this.farGroup.add(this.floorGrid);

    this.floorY = -420;
  }

  setFloorY(y) { this.floorY = y; }

  update(dt, elapsed, camera) {
    this.domeMat.uniforms.uTime.value = elapsed;
    this.gridMat.uniforms.uTime.value = elapsed;
    if (this.trafficMat) this.trafficMat.uniforms.uTime.value = elapsed;
    this.skyGroup.position.copy(camera.position);
    // Horizontal follow only: the skyline keeps its real altitude so climbing
    // above it looks like climbing above it.
    this.farGroup.position.set(camera.position.x, 0, camera.position.z);
    this.floorGrid.position.y = this.floorY;
  }

  /** Build a prefiltered environment map from the dome for PBR reflections. */
  buildEnvironment(renderer) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    const scene = new THREE.Scene();
    const dome = new THREE.Mesh(this.dome.geometry, this.domeMat);
    dome.scale.setScalar(500);
    scene.add(dome);
    const rt = pmrem.fromScene(scene, 0, 0.1, 1200);
    pmrem.dispose();
    scene.remove(dome);
    return rt.texture;
  }

  dispose() {
    this.group.traverse((o) => {
      o.geometry?.dispose?.();
      if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
      else o.material?.dispose?.();
    });
    this.windows?.dispose();
  }
}
